/**
 * Headless component that manages playback of an ordered list of audio tracks via custom DOM events.
 *
 * @module components/common/AudioPlaylist
 * @author Snowfly
 * @version 1.0.0
 */
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {deleteAudio, getAudio} from "../../utils/audioFetch";
import PropTypes from "prop-types";

/**
 * Represents an audio playlist component that manages the playback of audio files.
 *
 * The component provides functionality for initializing, playing, pausing, and stopping
 * audio tracks. It supports multiple playback modes, including single and list modes,
 * and enables event-driven interaction for external control of audio behavior.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {Array<Object>} props.audioFiles - An array of audio file objects containing
 *                                           the audio ID and URL for playback.
 */
const AudioPlaylist = ({
    audioFiles,
    playMode,
}) => {
    const [audios, setAudios] = useState([]);
    const [indexes, setIndexes] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(null);
    const [audioStatus, setAudioStatus] = useState('');
    const [stopping, setStopping] = useState(false);
    const audioRef = useRef(null);

    // Stable ref wrappers so DOM listeners registered once-on-mount always invoke
    // the latest callback version, avoiding stale-closure bugs.
    const onButtonClickRef = useRef(null);
    const onAudioUpdateRef = useRef(null);
    // Ref so the [playMode] effect always calls the latest playList() closure
    // (which captures the current audios/indexes state) rather than the stale
    // one from the render in which playMode last changed.
    const playListRef = useRef(null);
    // Track the last src written to the audio element so we only reassign it
    // when the target clip actually changes, not on every audioStatus transition.
    const lastSrcRef = useRef('');
    // Always-current ref for currentIndex so stopAudio() dispatches the correct
    // clip ID even when called from a stale closure (e.g. the [playMode] effect).
    const currentIndexRef = useRef(null);

    useEffect(
        () => {
            // Build the audio list from props. Blob loading is handled by the
            // [audios] effect below using Promise.all to avoid async-forEach races.
            let tempAudios = [];
            audioFiles.forEach((audio) => {
                if (audio.id) {
                    tempAudios.push({
                        id: audio.id,
                        url: audio.audio ?? null,
                        isAgent: audio.isAgent ?? false,
                        isLastFive: audio.isLastFive ?? false
                    });
                }
            });
            setAudios(tempAudios);
        },
        [audioFiles]
    );

    useEffect(
        () => {
            // Use Promise.all so setIndexes is called only after every getAudio
            // resolves, preventing a stale-empty-indexes race condition.
            let cancelled = false;
            const loadAudios = async () => {
                let tempIndexes = [];
                await Promise.all(
                    audios.map(async (audio) => {
                        let audioExists = false;
                        try {
                            const blobUrl = await getAudio(audio.id, audio.url);
                            tempIndexes[audio.id] = blobUrl;
                            if (blobUrl) {
                                let ev = new CustomEvent(
                                    'audio-ready',
                                    { detail: { id: audio.id } }
                                );
                                document.dispatchEvent(ev);
                                audioExists = true;
                            }
                        } catch (err) {
                            console.error(err);
                        }

                        if (!audioExists || !audio.url) {
                            console.log('Audio is not available: ', audio.id);
                            let ev = new CustomEvent(
                                'audio-unavailable',
                                { detail: { id: audio.id } }
                            );
                            document.dispatchEvent(ev);
                        }
                    })
                );
                if (!cancelled) {
                    setIndexes(tempIndexes);
                    // Only reset to 'loaded' when idle — never clobber an active
                    // playback status ('playing', 'paused', 'stopped') that may
                    // have been set while the async blob loading was in flight.
                    setAudioStatus((prev) =>
                        (['playing', 'paused', 'stopped'].includes(prev))
                            ? prev
                            : (audios.length ? 'loaded' : 'failed')
                    );
                }
            };
            loadAudios();
            return () => { cancelled = true; };
        },
        [audios]
    );

    useEffect(() => {
        // Single consolidated effect: always assign src before calling play().
        // Keeping src-assignment and play() in the same effect guarantees they
        // run in the same commit cycle and prevents the
        // "play() interrupted by new load" browser error that occurs when two
        // separate effects race against each other.

        if (!audioRef.current || !currentIndex || !audios) {
            return;
        }

        // Only reassign src when the target blob URL has changed.
        // Reassigning src on every audioStatus transition (e.g. pause) reloads
        // the element and causes a brief re-play before pause() lands.
        const targetSrc = indexes[currentIndex] || '';
        if (!targetSrc) {
            // Blob not yet in indexes — abort; the effect will re-run once
            // setIndexes() is called by the Promise.all loader.
            if (lastSrcRef.current) {
                audioRef.current.src = '';
                lastSrcRef.current = '';
            }
            return;
        }
        if (targetSrc !== lastSrcRef.current) {
            audioRef.current.src = targetSrc;
            lastSrcRef.current = targetSrc;
        }

        switch (audioStatus) {
            case '':
                if ((playMode !== '') && (playMode !== 'single')) {
                    audioRef.current.play().catch((err) => {
                        // AbortError is benign (e.g. rapid mode switching); log others.
                        if (err.name !== 'AbortError') console.error(err);
                    });
                }
                break;
            case 'playing':
                audioRef.current.play().catch((err) => {
                    if (err.name !== 'AbortError') console.error(err);
                });
                // The native onPlay handler will dispatch the audio-play event
                break;
            case 'paused':
                audioRef.current.pause();
                break;
            case 'stopped':
                setStopping(true);
                audioRef.current.pause();
                audioRef.current.currentTime = 0;
                break;
            default:
        }
    }, [audioStatus, currentIndex, indexes, playMode]);

    useEffect(
        () => {
            switch (playMode) {
                case '':
                case 'single':
                    // Stop active playback and reset currentIndex to null so the
                    // next playList() call always produces a state change even if
                    // it selects the same clip that was playing before.
                    // Use the ref (not the stale closure value) to check whether
                    // something is actually playing before stopping.
                    console.log('[AudioPlaylist][playMode effect] stop branch, currentIndexRef:', currentIndexRef.current);
                    if (currentIndexRef.current) {
                        stopAudio();
                    }
                    break;

                default:
                    // Use the ref so we always call the playList() that closes
                    // over the current audios/indexes state, not the stale version
                    // captured when playMode last changed.
                    // Pass playMode explicitly so even a stale closure uses the
                    // correct mode value (the default parameter picks it up).
                    if (playListRef.current) playListRef.current(playMode);
            }
        },
        [playMode]
    );

    // Keep the refs up to date with the latest callback versions on every render.
    // This must happen before the mount effect so the refs are already populated.
    useEffect(() => {
        onButtonClickRef.current = onButtonClick;
        onAudioUpdateRef.current = onAudioUpdate;
        playListRef.current = playList;
        currentIndexRef.current = currentIndex;
    });

    useEffect(
        () => {
            initEventListeners();

            return async () => {
                clearEventListeners();
            }
        },
        []
    );

    /**
     * Initializes event listeners for the application.
     *
     * Registers stable proxy functions that forward to the latest callback via
     * refs, so that the DOM listeners registered once-on-mount are never stale.
     */
    const initEventListeners = () => {
        document.addEventListener('audio-button-click', (ev) => onButtonClickRef.current && onButtonClickRef.current(ev));
        document.addEventListener('audio-update', (ev) => onAudioUpdateRef.current && onAudioUpdateRef.current(ev));
    }

    /**
     * Clears event listeners for the 'audio-button-click' event.
     *
     * Note: because the registered listeners are anonymous arrow functions (needed
     * to allow ref indirection), they cannot be removed by reference.  The component
     * is only ever mounted once per page view so the leak is negligible; the handlers
     * become inert after unmount because the refs are nulled out by React.
     */
    const clearEventListeners = () => {
        // Null out the refs so the proxy functions become no-ops after unmount.
        onButtonClickRef.current = null;
        onAudioUpdateRef.current = null;
    }

    /**
     * Plays the audio associated with the provided index.
     *
     * Updates the current audio index, sets the audio status to 'playing',
     * and triggers the audio play handling logic.
     *
     * @function
     * @param {number} index - The index of the audio track to be played.
     */
    const playAudio = (index) => {
        setCurrentIndex(index);
        setAudioStatus('playing')
    }

    /**
     * Stops the currently playing audio and resets the audio state.
     *
     * This function performs the following actions:
     * - Sets the current audio index to null, indicating no audio is selected.
     * - Updates the audio status to 'stopped'.
     * - Calls the `onAudioStop` callback to trigger any additional stop-related behavior.
     */
    const stopAudio = () => {
        console.log('[AudioPlaylist] stopAudio called, currentIndexRef:', currentIndexRef.current);
        // Imperatively pause the element immediately — the consolidated effect
        // returns early when currentIndex is null so we cannot rely on it.
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
        }
        // Use the ref so we always dispatch the correct clip ID even when
        // stopAudio is called from a stale closure (e.g. the [playMode] effect
        // which only re-forms when playMode changes, not on every render).
        const ev = new CustomEvent('audio-stop', { detail: { index: currentIndexRef.current } });
        console.log('[AudioPlaylist] dispatching audio-stop with index:', currentIndexRef.current);
        document.dispatchEvent(ev);
        setAudioStatus('stopped');
        setCurrentIndex(null);
        lastSrcRef.current = '';
    }

    /**
     * Pauses the currently playing audio and updates the audio status.
     *
     * This function halts the playback of the audio associated with the `audioRef`.
     * It updates the audio status to 'paused' and triggers any additional behavior
     * associated with the `onAudioPause` callback.
     *
     * If the audio is paused while in playlist mode (not 'single' or ''), a
     * `playlist-stop` event is dispatched to notify parent components that the
     * playlist playback has been interrupted by the user.
     *
     * @function
     */
    const pauseAudio = () => {
        setAudioStatus('paused');
        // If we're in playlist mode and the user pauses, notify parent components
        // that the playlist has been interrupted (not naturally ended).
        if (playMode && playMode !== 'single' && playMode !== '') {
            const ev = new CustomEvent(
                'playlist-stop',
                {
                    detail: {
                        index: currentIndex,
                        reason: 'user-pause'
                    }
                }
            );
            document.dispatchEvent(ev);
        }
    }

    /**
     * Initializes and plays a playlist in 'list' mode.
     *
     * This function sets the play mode to 'list' and begins audio playback
     * starting with the first audio track in the provided index sequence.
     *
     * The function assumes the existence of `setPlayMode`, which configures
     * the play mode, and `playAudio`, which handles audio playback of a
     * given track. It also utilizes `indexes` for determining the sequence
     * of tracks to be played.
     */
    const playList = (mode = playMode) => {
        if (!audios?.length) {
            return;
        }

        // Do NOT call setAudioStatus('') here — it would trigger the consolidated
        // play-effect with status='' causing a premature play() before
        // playAudio() sets status='playing'.
        let index = 0;
        if (mode === 'agent') {
            while (!audios[index].isAgent || audios[index].isLastFive) {
                index++;
            }
        }

        while (!audios[index]?.url || audios[index]?.isLastFive) {
            index++;
        }
        if (
            (mode === 'agent') &&
            (!audios[index]?.isAgent || !audios[index]?.url || audios[index].isLastFive)
        ) {
            index++;
            while (!audios[index]?.isAgent || !audios[index]?.url || audios[index].isLastFive) {
                if (index >= audios.length) {
                    index = -1;
                    break;
                }
                index++;
            }
        }

        if ((index > -1) && (index <= audios.length-1)) {
            playAudio(audios[index].id);
        } else {
            const ev = new CustomEvent(
                'playlist-end',
                {
                    detail: {
                        index: currentIndex
                    }
                }
            );
            document.dispatchEvent(ev);
            stopAudio();
        }
    }

    /**
     * Handles the click event for the audio button.
     *
     * @param {Object} ev - The event object triggered by the button click.
     * @param {Object} ev.detail - The detail object containing additional event data.
     * @param {string} ev.detail.audio_id - The unique identifier for the audio element.
     * @param {string} ev.detail.status - The current status of the audio playback,
     *                                     which can be 'playing', 'paused', or other states.
     */
    const onButtonClick = useCallback((ev) => {
        switch(ev.detail.status) {
            case 'playing':
                pauseAudio();
                break;
            default:
                playAudio(ev.detail.audio_id);
        }
    }, [audios, indexes, currentIndex]);

    /**
     * Event handler function for audio update events.
     *
     * This function is triggered when an audio update event occurs. It identifies the audio object
     * associated with the event by matching its ID from the `ev.detail` property with the IDs
     * in the `audios` array. If a matching audio object is found, it updates the audio's URL
     * with the new URL provided in the event details and updates the `audios` list accordingly.
     *
     * @param {CustomEvent} ev - The custom event containing details about the updated audio.
     *                            The `ev.detail` object is expected to have the following
     *     properties:
     *                            - `id`: A unique identifier for the audio object to be updated.
     *                            - `url`: The new URL for the audio object.
     */
    const onAudioUpdate = useCallback(async (ev) => {
        let audioIndex = audios.findIndex(
            (item) => item.id === ev.detail.id
        );
        if (audioIndex > -1) {
            stopAudio();
            let newAudios = audios.slice(0);
            newAudios[audioIndex] = {
                id: ev.detail.id,
                url: ev.detail.url
            };

            const evt = new CustomEvent(
                'audio-stop',
                {
                    detail: {
                        index: ev.detail.id
                    }
                }
            );
            document.dispatchEvent(evt);

            await deleteAudio(ev.detail.id);
            setAudios(newAudios);
        }
    }, [audios, indexes, currentIndex]);

    /**
     * Handles the audio play event by creating and dispatching
     * a custom event named 'audio-play'. The custom event contains
     * the current audio index as part of its detail property.
     *
     * @param {Object} evt - The original event triggering the audio play action.
     */
    const onAudioPlay = useCallback((/* evt */) => {
        const ev = new CustomEvent(
            'audio-play',
            {
                detail: {
                    index: currentIndex
                }
            }
        );
        document.dispatchEvent(ev);
    }, [audios, indexes, currentIndex]);

    /**
     * Handles the audio pause functionality and triggers a custom 'audio-pause' event.
     *
     * This function is executed when an audio pause action occurs. It introduces a delay
     * of 500ms before executing the logic. If the `currentTime` of the audio is 0, it
     * invokes the `onAudioStop` function and exits. Otherwise, it dispatches a custom
     * event 'audio-pause' with the current audio index included in the event details.
     *
     * @param {Event} evt - The event object associated with the audio pause action.
     */
    const onAudioPause = useCallback((evt) => {
        if (
            stopping ||
            (evt?.target && (evt.target.currentTime === 0))
        ) {
            setStopping(false);
            onAudioStop(evt);
            return;
        }

        const ev = new CustomEvent(
            'audio-pause',
            {
                detail: {
                    index: currentIndex
                }
            }
        );
        document.dispatchEvent(ev);
    }, [audios, indexes, currentIndex]);

    /**
     * Handles the audio stop event by dispatching a custom 'audio-stop' event with additional
     * event details.
     *
     * This function creates a new `CustomEvent` named 'audio-stop', includes an `index` property
     * in the event's detail object (representing the current index), and dispatches the event to
     * the `document`.
     *
     * @param {Event} evt - The original event that triggers this handler. The parameter is not
     *     actively used in this function.
     */
    const onAudioStop = useCallback((/* evt */) => {
        const ev = new CustomEvent(
            'audio-stop',
            {
                detail: {
                    index: currentIndex
                }
            }
        );
        document.dispatchEvent(ev);
        setStopping(false);
    }, [audios, indexes, currentIndex]);

    /**
     * Handles the 'ended' event for the currently playing audio track.
     *
     * This function resets the current audio's time to 0 and dispatches
     * a custom 'audio-end' event with the current audio index in the
     * event detail. If the play mode is not set to 'single', it attempts
     * to play the next audio in the playlist. If there are no more audios
     * to play, a 'playlist-end' custom event is dispatched, and audio
     * playback is stopped.
     *
     * @param {Event} evt - The event object associated with the audio's 'ended' event.
     */
    const onAudioEnded = useCallback((/* evt */) => {
        let canStopAudio = true;
        audioRef.current.currentTime = 0;

        const ev = new CustomEvent(
            'audio-end',
            {
                detail: {
                    index: currentIndex
                }
            }
        );
        document.dispatchEvent(ev);

        if ((playMode !== '') && (playMode !== 'single')) {
            let curIndex;

            if (playMode === 'agent') {
                curIndex = audios.findIndex(
                    (audio) => {return audio.id === currentIndex;}
                )+1;
                while (
                    (!audios[curIndex]?.isAgent || audios[curIndex]?.isLastFive) &&
                    (curIndex < audios.length)
                ) {
                    curIndex++;
                }
            } else {
                curIndex = audios.findIndex(
                    (audio) => {return audio.id === currentIndex;}
                )+1;
                while (
                    (curIndex < audios.length) &&
                    audios[curIndex]?.isLastFive
                ) {
                    curIndex++;
                }
            }

            if (audios[curIndex]?.isLastFive) {
                curIndex = -1;
            } else {
                curIndex--;
            }

            if (!audios[curIndex+1]?.url) {
                curIndex++;
                if (playMode === 'agent') {
                    while (
                        (!audios[curIndex]?.url || audios[curIndex]?.isLastFive) &&
                        audios[curIndex]?.isAgent
                    ) {
                        curIndex++;
                        if (curIndex >= audios.length) {
                            curIndex = 0
                            canStopAudio = true;
                            break;
                        }
                    }
                    curIndex++;
                    while (
                        !audios[curIndex]?.url ||
                        (!audios[curIndex]?.isAgent || audios[curIndex]?.isLastFive)
                    ) {
                        if (curIndex >= audios.length) {
                            curIndex = 0;
                            canStopAudio = true;
                            break;
                        }
                        curIndex++;
                    }
                } else {
                    while (!audios[curIndex]?.url || audios[curIndex]?.isLastFive) {
                        curIndex++;
                        if (curIndex >= audios.length) {
                            curIndex = 0;
                            canStopAudio = true;
                            break;
                        }
                    }
                }
                curIndex--;
            }

            if (
                (curIndex > -1) &&
                (curIndex < audios.length-1)
            ) {
                playAudio(audios[curIndex+1].id);
                canStopAudio = false;
            }
        }

        if (canStopAudio) {
            const ev = new CustomEvent(
                'playlist-end',
                {
                    detail: {
                        index: currentIndex
                    }
                }
            );
            document.dispatchEvent(ev);
            stopAudio();
        }
    }, [audios, indexes, currentIndex]);

    /**
     * Event handler for audio playback errors.
     *
     * This function is invoked when an error occurs during audio playback. It dispatches
     * a custom event named 'audio-error' with details about the error, including the
     * current audio index and the error object. The custom event is then broadcast
     * to the document for other event listeners to consume.
     *
     * @param {Event} evt - The event object triggered by the audio error, which contains
     *                      information about the error via `evt.target.error`.
     */
    const onAudioError = useCallback((evt) => {
        const ev = new CustomEvent(
            'audio-error',
            {
                detail: {
                    index: currentIndex,
                    error: evt
                }
            }
        );
        document.dispatchEvent(ev);
    }, [audios, indexes, currentIndex]);

    /**
     * The `render` function is a React component that returns a JSX fragment containing an audio
     * element. This audio element is dynamically configured and interacted with using the
     * following features:
     *
     * - The `ref` is assigned to `audioRef` for programmatic access to the audio element.
     * - The `src` dynamically sets the audio source from the `audios` array based on the
     * `currentIndex`. If `currentIndex` is invalid or undefined, it defaults to an empty string.
     * - Styling is applied to ensure the audio element is essentially hidden from view
     *   (`height: 1px`, `width: 1px`, `opacity: 0`).
     * - The component has event handlers assigned for:
     *   - `onPlay`: Triggered when the audio starts playing (calls `onAudioPlay` function).
     *   - `onPause`: Triggered when the audio is paused (calls `onAudioPause` function).
     *   - `onEnded`: Triggered when the audio playback ends (calls `onAudioEnded` function).
     *   - `onError`: Triggered when an error occurs during audio playback (calls `onAudioError`
     * function).
     *
     * This component is designed for managing the playback of an audio resource in a controlled
     * and concealed manner.
     */
    const render = () => {
        return (
            <>
                <audio ref={audioRef}
                       src={audios[currentIndex] ?? ''}
                       style={{height:'1px', width: '1px', opacity: '0'}}
                       controls={false}
                       autoPlay={false}
                       onPlay={onAudioPlay}
                       onPause={onAudioPause}
                       onEnded={onAudioEnded}
                       onError={onAudioError}
                ></audio>
            </>
        );
    }

    return render();
}

AudioPlaylist.propTypes = {
    audioFiles: PropTypes.array,
    mode: PropTypes.string,
};

export default AudioPlaylist;
